import React, { useState } from 'react';
import './ExportView.css';

function ExportView({ 
  students, 
  studentRsvps, 
  staff, 
  staffRsvps, 
  studentNonRespondents, 
  staffNonRespondents 
}) {
  const [exportType, setExportType] = useState('all-rsvps');
  const [includeDetails, setIncludeDetails] = useState(true);
  const [format, setFormat] = useState('csv');
  const [exportStatus, setExportStatus] = useState(null);
  
  // Generate CSV data based on selected type
  const generateCsvData = () => {
    let headers = [];
    let rows = [];
    
    // Generate the appropriate columns based on export type and options
    if (exportType === 'all-rsvps' || exportType === 'student-rsvps') {
      headers = ["Type", "Name", "ID", "Program", "RSVP Status", "Guest Count"];
      
      if (includeDetails) {
        headers.push("Pronunciation", "Pronunciation Audio", "Guests", "Last Updated");
      }
      
      // Add student RSVPs
      if (exportType === 'all-rsvps' || exportType === 'student-rsvps') {
        const studentsWithRsvp = students.map(student => {
          const rsvp = studentRsvps.find(r => r.studentId === student.id || r.id === student.id);
          return {
            ...student,
            rsvp: rsvp || null,
            hasConfirmedRsvp: rsvp && (rsvp.status === 'confirmed' || rsvp.completedSteps?.includes('confirmation'))
          };
        });
        
        studentsWithRsvp.forEach(student => {
          const row = [
            "Student",
            student.name || "",
            student.id || "",
            student.program || "",
            student.hasConfirmedRsvp ? "Confirmed" : "Not Confirmed",
            student.rsvp ? (student.rsvp.guestCount || "0") : "0"
          ];
          
          if (includeDetails) {
            // Add pronunciation text
            row.push(student.rsvp?.pronunciation || "");
            
            // Add pronunciation audio URL
            row.push(student.rsvp?.pronunciationAudio || "");
            
            // Add guest details
            if (student.rsvp && student.rsvp.guests && student.rsvp.guestCount > 0) {
              const guestDetails = student.rsvp.guests
                .slice(0, student.rsvp.guestCount)
                .map(guest => `${guest.name || 'Unknown'} (${guest.relationship || 'Unknown'})`)
                .join("; ");
              row.push(guestDetails);
            } else {
              row.push("");
            }
            
            // Add last updated date
            if (student.rsvp?.updatedAt) {
              const date = student.rsvp.updatedAt.toDate 
                ? student.rsvp.updatedAt.toDate().toLocaleString() 
                : new Date(student.rsvp.updatedAt).toLocaleString();
              row.push(date);
            } else {
              row.push("");
            }
          }
          
          rows.push(row);
        });
      }
    }
    
    // Add staff RSVPs
    if (exportType === 'all-rsvps' || exportType === 'staff-rsvps') {
      if (headers.length === 0) {
        headers = ["Type", "Name", "Username", "Title", "Email", "RSVP Status", "Guest Count"];
        
        if (includeDetails) {
          headers.push("Privacy Setting", "Guests", "Last Updated");
        }
      }
      
      const staffWithRsvp = staff.map(staffMember => {
        const rsvp = staffRsvps.find(r => 
          r.staffUsername === staffMember.username || 
          r.username === staffMember.username
        );
        
        return {
          ...staffMember,
          rsvp: rsvp || null,
          hasConfirmedRsvp: rsvp && (rsvp.status === 'confirmed' || rsvp.completedSteps?.includes('confirmation'))
        };
      });
      
      staffWithRsvp.forEach(staffMember => {
        const row = [
          "Staff",
          staffMember.name || "",
          staffMember.username || "",
          staffMember.title || "",
          staffMember.email || "",
          staffMember.hasConfirmedRsvp ? "Confirmed" : "Not Confirmed",
          staffMember.rsvp ? (staffMember.rsvp.guestCount || "0") : "0"
        ];
        
        if (includeDetails) {
          // Add privacy setting
          row.push(staffMember.rsvp?.privacyEnabled ? "Private" : "Standard");
          
          // Add guest details
          if (staffMember.rsvp && staffMember.rsvp.guests && staffMember.rsvp.guestCount > 0 && !staffMember.rsvp.isRedacted) {
            const guestDetails = staffMember.rsvp.guests
              .slice(0, staffMember.rsvp.guestCount)
              .map(guest => `${guest.name || 'Unknown'} (${guest.relationship || 'Unknown'})`)
              .join("; ");
            row.push(guestDetails);
          } else if (staffMember.rsvp?.isRedacted) {
            row.push("[Private - Details Hidden]");
          } else {
            row.push("");
          }
          
          // Add last updated date
          if (staffMember.rsvp?.updatedAt) {
            const date = staffMember.rsvp.updatedAt.toDate 
              ? staffMember.rsvp.updatedAt.toDate().toLocaleString() 
              : new Date(staffMember.rsvp.updatedAt).toLocaleString();
            row.push(date);
          } else {
            row.push("");
          }
        }
        
        rows.push(row);
      });
    }
    
    // Non-responders
    if (exportType === 'non-responders') {
      headers = ["Type", "Name", "ID/Username", "Program/Title", "Email"];
      
      // Add student non-responders
      studentNonRespondents.forEach(student => {
        rows.push([
          "Student",
          student.name || "",
          student.id || "",
          student.program || "",
          ""
        ]);
      });
      
      // Add staff non-responders
      staffNonRespondents.forEach(staffMember => {
        rows.push([
          "Staff",
          staffMember.name || "",
          staffMember.username || "",
          staffMember.title || "",
          staffMember.email || ""
        ]);
      });
    }
    
    // Guest list
    if (exportType === 'guest-list') {
      headers = ["Primary Attendee", "Type", "Guest Name", "Relationship"];
      
      // Add student guests
      studentRsvps
        .filter(rsvp => rsvp.guestCount > 0 && rsvp.guests)
        .forEach(rsvp => {
          const student = students.find(s => s.id === (rsvp.studentId || rsvp.id));
          const studentName = student ? student.name : "Unknown Student";
          
          rsvp.guests.slice(0, rsvp.guestCount).forEach(guest => {
            rows.push([
              studentName,
              "Student",
              guest.name || "Unknown",
              guest.relationship || ""
            ]);
          });
        });
      
      // Add staff guests
      staffRsvps
        .filter(rsvp => rsvp.guestCount > 0 && rsvp.guests && !rsvp.isRedacted)
        .forEach(rsvp => {
          const staffMember = staff.find(s => s.username === (rsvp.staffUsername || rsvp.username));
          const staffName = staffMember ? staffMember.name : "Unknown Staff";
          
          rsvp.guests.slice(0, rsvp.guestCount).forEach(guest => {
            rows.push([
              staffName,
              "Staff",
              guest.name || "Unknown",
              guest.relationship || ""
            ]);
          });
        });
    }
    
    // Name pronunciations
    if (exportType === 'pronunciations') {
      headers = ["Student Name", "Student ID", "Program", "Pronunciation Text", "Audio URL"];
      
      studentRsvps
        .filter(rsvp => rsvp.pronunciation || rsvp.pronunciationAudio)
        .forEach(rsvp => {
          const student = students.find(s => s.id === (rsvp.studentId || rsvp.id));
          
          if (student) {
            rows.push([
              student.name || "",
              student.id || "",
              student.program || "",
              rsvp.pronunciation || "",
              rsvp.pronunciationAudio || ""
            ]);
          }
        });
    }
    
    // Generate CSV content
    let csvContent = headers.join(",") + "\n";
    
    rows.forEach(row => {
      // Escape any fields with commas or quotes
      const escapedRow = row.map(field => {
        // Convert to string
        const str = String(field);
        
        // Check if field needs escaping
        if (str.includes('"') || str.includes(',') || str.includes('\n')) {
          return `"${str.replace(/"/g, '""')}"`;
        }
        return str;
      });
      
      csvContent += escapedRow.join(",") + "\n";
    });
    
    return csvContent;
  };
  
  // Generate JSON data
  const generateJsonData = () => {
    let data = {};
    
    if (exportType === 'all-rsvps') {
      const studentData = students.map(student => {
        const rsvp = studentRsvps.find(r => r.studentId === student.id || r.id === student.id);
        return {
          ...student,
          rsvp: rsvp || null,
          hasRsvp: !!rsvp,
          hasConfirmedRsvp: rsvp && (rsvp.status === 'confirmed' || rsvp.completedSteps?.includes('confirmation'))
        };
      });
      
      const staffData = staff.map(staffMember => {
        const rsvp = staffRsvps.find(r => 
          r.staffUsername === staffMember.username || 
          r.username === staffMember.username
        );
        
        return {
          ...staffMember,
          rsvp: rsvp || null,
          hasRsvp: !!rsvp,
          hasConfirmedRsvp: rsvp && (rsvp.status === 'confirmed' || rsvp.completedSteps?.includes('confirmation'))
        };
      });
      
      data = {
        students: studentData,
        staff: staffData,
        exportDate: new Date().toISOString(),
        summary: {
          totalStudents: students.length,
          studentRespondents: studentData.filter(s => s.hasRsvp).length,
          studentConfirmed: studentData.filter(s => s.hasConfirmedRsvp).length,
          totalStaff: staff.length,
          staffRespondents: staffData.filter(s => s.hasRsvp).length,
          staffConfirmed: staffData.filter(s => s.hasConfirmedRsvp).length,
          totalGuests: 
            studentData.reduce((acc, s) => acc + (s.rsvp?.guestCount || 0), 0) +
            staffData.reduce((acc, s) => acc + (s.rsvp?.guestCount || 0), 0),
          totalAttendees: 
            studentData.filter(s => s.hasConfirmedRsvp).length +
            staffData.filter(s => s.hasConfirmedRsvp).length +
            studentData.reduce((acc, s) => acc + (s.rsvp?.guestCount || 0), 0) +
            staffData.reduce((acc, s) => acc + (s.rsvp?.guestCount || 0), 0)
        }
      };
    } else if (exportType === 'student-rsvps') {
      data = students.map(student => {
        const rsvp = studentRsvps.find(r => r.studentId === student.id || r.id === student.id);
        return {
          ...student,
          rsvp: rsvp || null,
          hasRsvp: !!rsvp,
          hasConfirmedRsvp: rsvp && (rsvp.status === 'confirmed' || rsvp.completedSteps?.includes('confirmation'))
        };
      });
    } else if (exportType === 'staff-rsvps') {
      data = staff.map(staffMember => {
        const rsvp = staffRsvps.find(r => 
          r.staffUsername === staffMember.username || 
          r.username === staffMember.username
        );
        
        return {
          ...staffMember,
          rsvp: rsvp || null,
          hasRsvp: !!rsvp,
          hasConfirmedRsvp: rsvp && (rsvp.status === 'confirmed' || rsvp.completedSteps?.includes('confirmation'))
        };
      });
    } else if (exportType === 'non-responders') {
      data = {
        students: studentNonRespondents,
        staff: staffNonRespondents,
        exportDate: new Date().toISOString()
      };
    } else if (exportType === 'guest-list') {
      const guests = [];
      
      // Add student guests
      studentRsvps
        .filter(rsvp => rsvp.guestCount > 0 && rsvp.guests)
        .forEach(rsvp => {
          const student = students.find(s => s.id === (rsvp.studentId || rsvp.id));
          const studentName = student ? student.name : "Unknown Student";
          
          rsvp.guests.slice(0, rsvp.guestCount).forEach(guest => {
            guests.push({
              primaryAttendee: studentName,
              type: "Student",
              name: guest.name || "Unknown",
              relationship: guest.relationship || "",
              studentId: student?.id || ""
            });
          });
        });
      
      // Add staff guests
      staffRsvps
        .filter(rsvp => rsvp.guestCount > 0 && rsvp.guests && !rsvp.isRedacted)
        .forEach(rsvp => {
          const staffMember = staff.find(s => s.username === (rsvp.staffUsername || rsvp.username));
          const staffName = staffMember ? staffMember.name : "Unknown Staff";
          
          rsvp.guests.slice(0, rsvp.guestCount).forEach(guest => {
            guests.push({
              primaryAttendee: staffName,
              type: "Staff",
              name: guest.name || "Unknown",
              relationship: guest.relationship || "",
              staffUsername: staffMember?.username || ""
            });
          });
        });
        
      data = guests;
    } else if (exportType === 'pronunciations') {
      data = studentRsvps
        .filter(rsvp => rsvp.pronunciation || rsvp.pronunciationAudio)
        .map(rsvp => {
          const student = students.find(s => s.id === (rsvp.studentId || rsvp.id));
          
          return {
            name: student?.name || "",
            studentId: student?.id || "",
            program: student?.program || "",
            pronunciation: rsvp.pronunciation || "",
            pronunciationAudio: rsvp.pronunciationAudio || ""
          };
        });
    }
    
    return JSON.stringify(data, null, 2);
  };
  
  // Handle export
  const handleExport = () => {
    try {
      setExportStatus({ type: 'loading', message: 'Generating export file...' });
      
      let data, mimeType, fileExtension;
      
      if (format === 'csv') {
        data = generateCsvData();
        mimeType = 'text/csv';
        fileExtension = 'csv';
      } else {
        data = generateJsonData();
        mimeType = 'application/json';
        fileExtension = 'json';
      }
      
      const blob = new Blob([data], { type: mimeType });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      
      // Create file name based on current date and export type
      const date = new Date().toISOString().split('T')[0];
      const fileName = `cte-awards-${exportType}-${date}.${fileExtension}`;
      
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      setExportStatus({ 
        type: 'success', 
        message: `Export successful! Downloaded as ${fileName}` 
      });
      
      // Clear status after 5 seconds
      setTimeout(() => {
        setExportStatus(null);
      }, 5000);
    } catch (error) {
      console.error("Export error:", error);
      setExportStatus({ 
        type: 'error', 
        message: `Export failed: ${error.message}` 
      });
    }
  };
  
  return (
    <div className="export-view-container">
      <h1>Export Data</h1>
      
      <div className="export-options">
        <div className="option-group">
          <h3>Export Type</h3>
          <div className="radio-group">
            <div className="radio-option">
              <input 
                type="radio" 
                id="all-rsvps" 
                name="export-type"
                value="all-rsvps"
                checked={exportType === 'all-rsvps'}
                onChange={(e) => setExportType(e.target.value)}
              />
              <label htmlFor="all-rsvps">All RSVPs</label>
            </div>
            
            <div className="radio-option">
              <input 
                type="radio" 
                id="student-rsvps" 
                name="export-type"
                value="student-rsvps"
                checked={exportType === 'student-rsvps'}
                onChange={(e) => setExportType(e.target.value)}
              />
              <label htmlFor="student-rsvps">Student RSVPs Only</label>
            </div>
            
            <div className="radio-option">
              <input 
                type="radio" 
                id="staff-rsvps" 
                name="export-type"
                value="staff-rsvps"
                checked={exportType === 'staff-rsvps'}
                onChange={(e) => setExportType(e.target.value)}
              />
              <label htmlFor="staff-rsvps">Staff RSVPs Only</label>
            </div>
            
            <div className="radio-option">
              <input 
                type="radio" 
                id="non-responders" 
                name="export-type"
                value="non-responders"
                checked={exportType === 'non-responders'}
                onChange={(e) => setExportType(e.target.value)}
              />
              <label htmlFor="non-responders">Non-Responders</label>
            </div>
            
            <div className="radio-option">
              <input 
                type="radio" 
                id="guest-list" 
                name="export-type"
                value="guest-list"
                checked={exportType === 'guest-list'}
                onChange={(e) => setExportType(e.target.value)}
              />
              <label htmlFor="guest-list">Guest List</label>
            </div>
            
            <div className="radio-option">
              <input 
                type="radio" 
                id="pronunciations" 
                name="export-type"
                value="pronunciations"
                checked={exportType === 'pronunciations'}
                onChange={(e) => setExportType(e.target.value)}
              />
              <label htmlFor="pronunciations">Name Pronunciations</label>
            </div>
          </div>
        </div>
        
        <div className="option-group">
          <h3>Export Format</h3>
          <div className="radio-group">
            <div className="radio-option">
              <input 
                type="radio" 
                id="csv-format" 
                name="format"
                value="csv"
                checked={format === 'csv'}
                onChange={(e) => setFormat(e.target.value)}
              />
              <label htmlFor="csv-format">CSV (for Excel)</label>
            </div>
            
            <div className="radio-option">
              <input 
                type="radio" 
                id="json-format" 
                name="format"
                value="json"
                checked={format === 'json'}
                onChange={(e) => setFormat(e.target.value)}
              />
              <label htmlFor="json-format">JSON (for developers)</label>
            </div>
          </div>
        </div>
        
        <div className="option-group">
          <h3>Include Details</h3>
          <div className="checkbox-option">
            <input 
              type="checkbox" 
              id="include-details" 
              checked={includeDetails}
              onChange={(e) => setIncludeDetails(e.target.checked)}
            />
            <label htmlFor="include-details">Include full details (guests, dates, etc.)</label>
          </div>
        </div>
      </div>
      
      <div className="export-button-container">
        <button 
          className="export-button"
          onClick={handleExport}
          disabled={exportStatus?.type === 'loading'}
        >
          {exportStatus?.type === 'loading' ? 'Generating...' : 'Export Data'}
        </button>
      </div>
      
      {exportStatus && (
        <div className={`export-status ${exportStatus.type}`}>
          <p>{exportStatus.message}</p>
          {exportStatus.type === 'success' && <span className="success-icon">✓</span>}
          {exportStatus.type === 'error' && <span className="error-icon">✗</span>}
          {exportStatus.type !== 'loading' && (
            <button 
              className="dismiss-button"
              onClick={() => setExportStatus(null)}
            >
              Dismiss
            </button>
          )}
        </div>
      )}
      
      <div className="export-summary">
        <h3>Export Summary</h3>
        <div className="summary-stats">
          <div className="stat-item">
            <span className="stat-label">Total Students:</span>
            <span className="stat-value">{students.length}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Student RSVPs:</span>
            <span className="stat-value">
              {studentRsvps.filter(rsvp => 
                rsvp.status === "confirmed" || rsvp.completedSteps?.includes("confirmation")
              ).length}
            </span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Total Staff:</span>
            <span className="stat-value">{staff.length}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Staff RSVPs:</span>
            <span className="stat-value">
              {staffRsvps.filter(rsvp => 
                rsvp.status === "confirmed" || rsvp.completedSteps?.includes("confirmation")
              ).length}
            </span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Total Guests:</span>
            <span className="stat-value">
              {studentRsvps.reduce((acc, rsvp) => acc + (rsvp.guestCount || 0), 0) + 
               staffRsvps.reduce((acc, rsvp) => acc + (rsvp.guestCount || 0), 0)}
            </span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Expected Attendees:</span>
            <span className="stat-value">
              {studentRsvps.filter(rsvp => 
                rsvp.status === "confirmed" || rsvp.completedSteps?.includes("confirmation")
              ).length +
               staffRsvps.filter(rsvp => 
                 rsvp.status === "confirmed" || rsvp.completedSteps?.includes("confirmation")
               ).length +
               studentRsvps.reduce((acc, rsvp) => acc + (rsvp.guestCount || 0), 0) + 
               staffRsvps.reduce((acc, rsvp) => acc + (rsvp.guestCount || 0), 0)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ExportView;